import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Button, CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import { useState } from 'react';
import ReactFlagsSelect from "react-flags-select";
import Notification from '../../components/Notification';
import ListPage from '../listPage/ListPage';
import './LoginPage.css';

// API Configuration - Update this with your actual API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const LoginPage = () => {
  // Step 1: Username and Password
  const [webUsername, setWebUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Step 2: Access Code
  const [accessCode, setAccessCode] = useState('');
  
  // UI State
  const [select, setSelect] = useState("GB");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1 = credentials, 2 = access code
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [expiresIn, setExpiresIn] = useState(0);

  // Notification State
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
    title: ''
  });
  
  // User data for authenticated session
  const [userData, setUserData] = useState(null);

  const onSelect = (code) => setSelect(code);

  // Notification Helper Functions
  const showNotification = (message, severity = 'info', title = '') => {
    setNotification({
      open: true,
      message,
      severity,
      title
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Step 1: Validate credentials and request access code
  const handleStep1Submit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/web-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          web_username: webUsername.trim(),
          password: password
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Success - move to step 2
        showNotification(
          result.data.message || 'Access code sent to your mobile app!',
          'success',
          'Success'
        );
        setExpiresIn(result.data.expires_in_seconds || 300);
        setCurrentStep(2);

        // Clear password for security
        setPassword('');
      } else {
        // Error
        const errorMessage = result.errors && result.errors.length > 0
          ? result.errors.join(', ')
          : 'Login failed. Please check your credentials.';
        showNotification(errorMessage, 'error', 'Login Failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      showNotification('Network error. Please check your connection and try again.', 'error', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify access code and complete login
  const handleStep2Submit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login-with-code-web`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          web_username: webUsername.trim(),
          access_code: accessCode.trim()
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Login successful
        const { user, access_token, refresh_token } = result.data;

        // Store tokens in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('user', JSON.stringify(user));

        // Update state
        setUserData(user);
        setIsLoggedIn(true);
        showNotification('Login successful!', 'success', 'Authentication Successful');
      } else {
        // Error
        const errorMessage = result.errors && result.errors.length > 0
          ? result.errors.join(', ')
          : 'Invalid access code. Please try again.';
        showNotification(errorMessage, 'error', 'Access Code Invalid');
      }
    } catch (err) {
      console.error('Access code verification error:', err);
      showNotification('Network error. Please check your connection and try again.', 'error', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Go back to step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
    setAccessCode('');
    setNotification(prev => ({ ...prev, open: false }));
  };

  
  return (
    <Box>
      {/* Notification Component */}
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
        onClose={closeNotification}
        position="top-right"
      />

      {isLoggedIn ? (
        <ListPage userData={userData} />
      ) : (
        <div className='body loginwrapper'>
          {/* Login form */}
          <div className='login-overlay' />
          <div className='login_form'>
            <div className="lang">
              {/* Language selector */}
              <Grid container justifyContent={'flex-end'} spacing={1}>
                <Grid>
                  <ReactFlagsSelect
                    className=''
                    selected={select}
                    onSelect={onSelect}
                    countries={["GB", "FR", "ES", "RU", "TR", "CN"]}
                    customLabels={{ GB: "EN-GB", FR: "FR", ES: "ES", RU: "RU", TR: "TR", CN: "CN" }}
                  />
                </Grid>
              </Grid>
              <div className="heading">
                <div className="heading__image"></div>
              </div>
            </div>
            
            <div className="formBody">

              {/* Step 1: Username and Password */}
              {currentStep === 1 && (
                <Box component="form" padding={'0px 10px'} onSubmit={handleStep1Submit}>

                  <TextField
                    size='small'
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '0.3em',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '& input::placeholder': {
                          fontSize: '14px',
                          fontWeight: '500'
                        },
                      }
                    }}
                    InputProps={{ 
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize='small' />
                        </InputAdornment>
                      ) 
                    }}
                    required
                    fullWidth
                    placeholder="Enter username"
                    id="web_username"
                    name="web_username"
                    value={webUsername}
                    onChange={(e) => setWebUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    disabled={loading}
                  />
                  
                  <TextField
                    size='small'
                    sx={{
                      backgroundColor: 'white',
                      mt: 3,
                      borderRadius: '0.3em',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '& input::placeholder': {
                          fontSize: '14px',
                          fontWeight: '500'
                        },
                      }
                    }}
                    InputProps={{ 
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ) 
                    }}
                    placeholder='Enter password'
                    required
                    fullWidth
                    name="password"
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  
                  <Button
                    className='btn-success'
                    type="submit"
                    fullWidth
                    disabled={loading || !webUsername || !password}
                    sx={{ 
                      mt: 3, 
                      mb: 2, 
                      p: 1, 
                      colors: 'white',
                      fontWeight: 'bold',
                      position: 'relative'
                    }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Verifying...
                      </>
                    ) : (
                      'LOG IN'
                    )}
                  </Button>
                </Box>
              )}

              {/* Step 2: Access Code */}
              {currentStep === 2 && (
                <Box component="form" padding={'0px 10px'} onSubmit={handleStep2Submit}>

                  <TextField
                    size='small'
                    sx={{
                      backgroundColor: 'white',
                      mt: 3,
                      borderRadius: '0.3em',
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '& input::placeholder': {
                          fontSize: '14px',
                          fontWeight: '500'
                        },
                      }
                    }}
                    InputProps={{ 
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ) 
                    }}
                    required
                    fullWidth
                    placeholder="Enter confirmation code"
                    id="access_code"
                    name="access_code"
                    value={accessCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only digits
                      if (value.length <= 6) {
                        setAccessCode(value);
                      }
                    }}
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]{6}'
                    }}
                    autoFocus
                    disabled={loading}
                  />
                  
                  <Button
                    className='btn-success'
                    type="submit"
                    fullWidth
                    disabled={loading || accessCode.length !== 6}
                    sx={{ 
                      mt: 3, 
                      mb: 2, 
                      p: 1, 
                      colors: 'white',
                      fontWeight: 'bold',
                      position: 'relative'
                    }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Verifying...
                      </>
                    ) : (
                      'Send'
                    )}
                  </Button>
                </Box>
              )}
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default LoginPage;